import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { Component, computed, effect, inject, input, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { StExpansionPanelComponent } from '@src/suite/common/components/st-expansion-panel/st-expansion-panel.component';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { OPTION_BLANK } from '@src/suite/common/enums/constants';
import { FieldDefValidations, FieldGroup } from '@src/suite/common/enums/enums';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectEnforceRequiredFieldsOnCreate, selectROGroupedFields } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { selectRundownTemplates, selectStoryTemplates } from '@src/suite/common/store/records/records.selectors';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';
import { calculateRequiredFieldGroups } from '@src/suite/common/utils/record-utils';
import { calculateDuration } from '@src/suite/common/utils/timing';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { DateTime, Duration } from 'luxon';
import { firstValueFrom } from 'rxjs';

import { AddressFormComponent } from '../../controls/address-form/address-form';
import { CheckBoxComponent } from '../../controls/check-box/check-box';
import { CheckListComponent } from '../../controls/check-list/check-list';
import { ComboBoxComponent } from '../../controls/combo-box/combo-box';
import { DatePickerComponent } from '../../controls/date-picker/date-picker';
import { DateTimePickerComponent } from '../../controls/date-time-picker/date-time-picker';
import { DateTimeRangeComponent } from '../../controls/date-time-range/date-time-range';
import { DurationSecondsInputFieldComponent } from '../../controls/duration-seconds-input-field/duration-seconds-input-field';
import { StTextAreaComponent } from '../../controls/st-text-area/st-text-area';
import { StTextAreaOverlayComponent } from '../../controls/st-text-area/text-area-overlay/st-text-area-overlay';
import { StaffIDListMultipleComponent } from '../../controls/staff-id-list/staff-id-list-multiple';
import { TextFieldComponent } from '../../controls/text-field/text-field';
import { TreeComponent } from '../../controls/tree/tree';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { SimpleSearchBoxComponent } from '../search-box/simple-search-box/simple-search-box';

@Component({
    selector: 'st-rundown-properties',
    imports: [
        AddressFormComponent,
        CdkOverlayOrigin,
        CheckBoxComponent,
        CheckListComponent,
        ClickStopPropagationDirective,
        ComboBoxComponent,
        DatePickerComponent,
        DateTimePickerComponent,
        DateTimeRangeComponent,
        DurationSecondsInputFieldComponent,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatIcon,
        MatIconButton,
        MatTooltip,
        SimpleSearchBoxComponent,
        StaffIDListMultipleComponent,
        StTextAreaComponent,
        StTextAreaOverlayComponent,
        TextFieldComponent,
        TreeComponent,
    ],
    templateUrl: './rundown-properties.html',
    styleUrl: './rundown-properties.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-rundown-properties',
    },
})
export class RundownPropertiesComponent {
    // injects
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    modalService = inject(ModalService);
    visualStateService = inject(VisualStateService);
    rundownService = inject(RundownService);

    // basic properties
    PSTFieldNames = PSTFieldNames;
    FieldGroup = FieldGroup;
    FieldDefValidations = FieldDefValidations;
    fieldsToNotOverrideFromTemplate = new Set<PSTFieldNames>([PSTFieldNames.PublishingDays, PSTFieldNames.MOSroBlock, PSTFieldNames.MOSroAllow, PSTFieldNames.AutoCreateDays]);

    constructor() {
        effect(() => {
            if (this.record()) {
                const newRec = {
                    ...this.record(),
                };
                if (newRec.OnAir) {
                    this.readonlyProps.update(m => {
                        m.set(PSTFieldNames.LTTRecording, true);
                        m.set(PSTFieldNames.LTTPaused, true);
                        return m;
                    });
                    newRec.LTTRecording = false;
                    newRec.LTTPaused = false;
                } else {
                    if (newRec.LTTRecording) {
                        this.readonlyProps.update(m => {
                            m.set(PSTFieldNames.OnAir, true);
                            m.set(PSTFieldNames.LTTRecording, false);
                            m.set(PSTFieldNames.LTTPaused, false);
                            return m;
                        });
                    } else {
                        this.readonlyProps.update(m => {
                            m.set(PSTFieldNames.LTTRecording, false);
                            m.set(PSTFieldNames.LTTPaused, true);
                            return m;
                        });
                        newRec.LTTPaused = false;
                    }
                }

                if (newRec.FixedDuration == null) {
                    newRec.FixedDuration = true;
                }

                this.localRecord.set(newRec);
            }
        });
        this.loadRundownTemplates();
        this.loadStoryTemplates();
    }

    // inputs
    readonly record = input<StorytellingRundownRecord>();
    readonly options = input<{
        propsToIgnore?: PSTFieldNames[];
    }>();

    // view children
    readonly accordion = viewChild(MatAccordion);

    // computed
    readonly title = computed(() => this.localRecord()?.Title);
    readonly startTime = computed(() => this.localRecord()?.StartTime);
    readonly endTime = computed(() => this.localRecord()?.EndTime);
    readonly groups = computed(() => {
        const roPropertyGroups = [
            FieldGroup.BASIC_PROPERTIES,
            FieldGroup.OTHER_PROPERTIES,
            FieldGroup.ARCHIVING_PROPERTIES,
            FieldGroup.MOS_PROPERTIES,
            FieldGroup.DIGITAL_PUBLISHING_PROPERTIES,
            FieldGroup.ROW_PROPERTIES,
            FieldGroup.AUTOCREATE_PROPERTIES,
        ];
        return Object.keys(this.groupedFields())
            .filter(group => roPropertyGroups.includes(group as FieldGroup))
            .sort((a, b) => roPropertyGroups.indexOf(a as FieldGroup) - roPropertyGroups.indexOf(b as FieldGroup));
    });

    readonly groupedFields = computed(() => {
        let groupedFields = this.store.selectSignal(selectROGroupedFields)();

        if (this.options()?.propsToIgnore) {
            groupedFields = Object.keys(groupedFields).reduce(
                (acc, group) => {
                    acc[group] = groupedFields[group].filter(field => !this.options()?.propsToIgnore.includes(field.ID as PSTFieldNames));
                    return acc;
                },
                {} as Record<string, FieldDef[]>
            );
        }

        if (this.searchTerm()?.trim()) {
            const searchTerm = this.searchTerm()?.trim()?.toLowerCase();
            groupedFields = Object.keys(groupedFields).reduce(
                (acc, group) => {
                    acc[group] = groupedFields[group].filter(field => field?.DefaultCaption?.toLowerCase()?.includes(searchTerm) || field?.ID?.toLowerCase()?.includes(searchTerm));
                    return acc;
                },
                {} as Record<string, FieldDef[]>
            );
        }
        return groupedFields;
    });

    readonly selectedStoryTemplateGuid = computed(() => this.localRecord()?.ScriptTemplate ?? OPTION_BLANK);
    readonly storyTemplatesList = computed(() => this.storyTemplates()?.map(template => ({ id: template.Guid, title: template.Title, selectable: true })) ?? []);
    readonly selectedStoryTemplate = computed(() => this.storyTemplatesList().find(template => template.id === this.selectedStoryTemplateGuid()));
    readonly selectedRundownTemplateGuid = computed(() => this.localRecord()?.SourceGUID ?? OPTION_BLANK);
    readonly rundownTemplatesList = computed(() => this.rundownTemplates().map(template => ({ id: template.Guid, title: template.Title, selectable: true })));
    readonly selectedRundownTemplate = computed(() => this.rundownTemplatesList().find(template => template.id === this.selectedRundownTemplateGuid()));
    readonly duration = computed(() => {
        return calculateDuration(this.startTime(), this.endTime());
    });
    readonly rundownMinDate = computed(() => this.localRecord()?.[PSTFieldNames.PSTRundownStartTime]);
    readonly rundownArchived = computed(() => {
        return this.localRecord()?.Path?.isArchivePath;
    });

    readonly collapseAllLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebCollapseAllLabel))();
    });

    readonly expandAllLabel = computed(() => {
        return this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebExpandAllLabel))();
    });

    readonly requiredFieldGroups = computed(() => {
        return calculateRequiredFieldGroups(this.record(), this.rundownService);
    });

    // store signals
    public languageSettings = this.store.selectSignal(selectI18N);
    public enforceRequiredFieldsOnCreate = this.store.selectSignal(selectEnforceRequiredFieldsOnCreate);
    public readonly storyTemplates = computed(() => this.store.selectSignal(selectStoryTemplates)());
    public readonly rundownTemplates = computed(() => this.store.selectSignal(selectRundownTemplates)());

    // signals
    public readonly localRecord = signal<StorytellingRundownRecord>(null);
    private readonly searchTerm = signal<string>('');
    readonly readonlyProps = signal(new Map<string, boolean>());

    // outputs
    readonly recordChanged = output<StorytellingRundownRecord>();
    readonly propertyChanged = output<{ field: PSTFieldNames; value: any }>();

    // methods
    updateRundownTemplate(value: any) {
        const updateFieldFromTemplate = (fieldValue: unknown, field: PSTFieldNames) => {
            switch (field) {
                case PSTFieldNames.MOSroEdDur: {
                    // MOSroEdDur fieldValue is in seconds when coming from the template
                    const duration = Duration.fromObject({ seconds: fieldValue as number });
                    this.localRecord.update(rec => ({ ...rec, [field]: duration }));
                    this.propertyChanged.emit({ field: field, value: duration });
                    return;
                }
                case PSTFieldNames.PreventArchiveOrPurge:
                case PSTFieldNames.OpenEndedBroadcast:
                case PSTFieldNames.AutoArchive:
                case PSTFieldNames.ContinuousContent:
                case PSTFieldNames.EnableCTOS: {
                    if (this.dependentArchiveProperties.has(field)) {
                        const dependentFields = this.dependentArchiveProperties.get(field);
                        const dependentFieldsToReset = dependentFields.filter(f => this.localRecord()?.[f]);
                        if (dependentFieldsToReset.length > 0) {
                            dependentFieldsToReset.forEach(f => {
                                this.localRecord.update(rec => ({ ...rec, [f]: false }));
                                this.propertyChanged.emit({ field: f, value: false });
                            });
                        }
                    }
                    break;
                }
            }
            this.localRecord.update(rec => ({ ...rec, [field]: fieldValue }));
            this.propertyChanged.emit({ field: field as PSTFieldNames, value: fieldValue });
        };
        const template = this.rundownTemplates().find(t => t.Guid === value.id);
        if (template) {
            Object.keys(template).forEach(field => {
                if (template[field] != null && !this.fieldsToNotOverrideFromTemplate.has(field as PSTFieldNames)) {
                    updateFieldFromTemplate(template[field], field as PSTFieldNames);
                }
            });
            this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.PSTSourceGUID]: template.Guid }));
            this.propertyChanged.emit({ field: PSTFieldNames.PSTSourceGUID, value: template.Guid });
            this.recordChanged.emit(this.localRecord());
        }
    }

    async updateRundown(value: any, field: PSTFieldNames) {
        switch (field) {
            case PSTFieldNames.Title: {
                value = value?.trim();
                break;
            }
            case PSTFieldNames.PSTRundownStartTime: {
                value = DateTime.isDateTime(value) ? (value as DateTime) : null;
                // update end time if before start time
                if (this.localRecord()?.EndTime < value) {
                    const endTime = value?.plus({ minutes: 30 });
                    this.localRecord.update(rec => ({ ...rec, EndTime: endTime }));
                    this.propertyChanged.emit({ field: PSTFieldNames.PSTRundownEndTime, value: endTime });
                }
                this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.MOSroEdStart]: value }));
                this.propertyChanged.emit({ field: PSTFieldNames.MOSroEdStart, value });
                const duration = calculateDuration(value, this.endTime());
                this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.MOSroEdDur]: duration }));
                this.propertyChanged.emit({ field: PSTFieldNames.MOSroEdDur, value: duration });
                //update mod time to rundown start time like in ENPS desktop client
                this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.PSTModTime]: value }));
                this.propertyChanged.emit({ field: PSTFieldNames.PSTModTime, value });
                break;
            }
            case PSTFieldNames.PSTRundownEndTime: {
                value = DateTime.isDateTime(value) ? (value as DateTime) : null;
                const duration = calculateDuration(this.startTime(), value);
                this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.MOSroEdDur]: duration }));
                this.propertyChanged.emit({ field: PSTFieldNames.MOSroEdDur, value: duration });
                break;
            }
            case PSTFieldNames.OnAir: {
                this.readonlyProps.update(m => {
                    m.set(PSTFieldNames.LTTRecording, value);
                    m.set(PSTFieldNames.LTTPaused, value);
                    return m;
                });
                // deselect LTTRecording and LTTPaused
                if (value) {
                    this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.LTTRecording]: false, [PSTFieldNames.LTTPaused]: false }));
                    this.propertyChanged.emit({ field: PSTFieldNames.LTTRecording, value: false });
                    this.propertyChanged.emit({ field: PSTFieldNames.LTTPaused, value: false });
                }
                break;
            }
            case PSTFieldNames.LTTRecording: {
                this.readonlyProps.update(m => {
                    m.set(PSTFieldNames.OnAir, value);
                    return m;
                });
                // deselect OnAir
                if (value) {
                    this.localRecord.update(rec => ({ ...rec, [PSTFieldNames.OnAir]: false }));
                    this.propertyChanged.emit({ field: PSTFieldNames.OnAir, value: false });
                }
                break;
            }
            case PSTFieldNames.PreventArchiveOrPurge:
            case PSTFieldNames.OpenEndedBroadcast:
            case PSTFieldNames.AutoArchive:
            case PSTFieldNames.ContinuousContent:
            case PSTFieldNames.EnableCTOS: {
                this.localRecord.update(rec => ({ ...rec, [field]: value }));
                if (this.dependentArchiveProperties.has(field)) {
                    const dependentFields = this.dependentArchiveProperties.get(field);
                    const dependentFieldsToReset = dependentFields.filter(f => this.localRecord()?.[f]);
                    if (dependentFieldsToReset.length > 0) {
                        let fieldDescription = this.languageSettings()[`PSTWebArchiveModal${dependentFieldsToReset[0]}`];
                        if (field === PSTFieldNames.PreventArchiveOrPurge) {
                            fieldDescription = this.languageSettings().PSTWebArchiveModalSetToArchive;
                        }
                        const content = this.languageSettings().PSTWebArchiveModalResetDependentFields.replace('{fieldDescription}', fieldDescription);
                        // the whole modal result should be syncronous
                        const result = await firstValueFrom(
                            this.modalService.openModal({
                                title: this.languageSettings().PSTWebModalWarningTitle,
                                content,
                            })?.result
                        );

                        if (result === ModalResponseCodes.OK) {
                            dependentFieldsToReset.forEach(f => {
                                this.localRecord.update(rec => ({ ...rec, [f]: false }));
                                this.propertyChanged.emit({ field: f, value: false });
                            });
                        } else {
                            // need to revert the change
                            this.localRecord.update(rec => ({ ...rec, [field]: false }));
                            this.propertyChanged.emit({ field, value: false });
                            this.recordChanged.emit(this.localRecord());
                            return;
                        }
                    }
                }
            }
        }

        // finally update the record and emit the property changed event
        this.localRecord.update(rec => ({ ...rec, [field]: value }));
        this.propertyChanged.emit({ field, value });
        this.recordChanged.emit(this.localRecord());
    }

    readonly dependentArchiveProperties = new Map<PSTFieldNames, PSTFieldNames[]>([
        [PSTFieldNames.PreventArchiveOrPurge, [PSTFieldNames.OpenEndedBroadcast, PSTFieldNames.AutoArchive, PSTFieldNames.ContinuousContent, PSTFieldNames.EnableCTOS]],
        [PSTFieldNames.OpenEndedBroadcast, [PSTFieldNames.PreventArchiveOrPurge, PSTFieldNames.ContinuousContent]],
        [PSTFieldNames.AutoArchive, [PSTFieldNames.PreventArchiveOrPurge]],
        [PSTFieldNames.ContinuousContent, [PSTFieldNames.PreventArchiveOrPurge, PSTFieldNames.OpenEndedBroadcast]],
        [PSTFieldNames.EnableCTOS, [PSTFieldNames.PreventArchiveOrPurge]],
    ]);

    checkboxValueChanged(value: boolean, field: string) {
        const enpsField = field as PSTFieldNames;

        this.updateRundown(value, enpsField);
    }

    valueChanged(value: any, field: string) {
        this.updateRundown(value, field as PSTFieldNames);
    }

    timezoneChanged(fieldId: string, event: any) {
        this.localRecord.update(val => {
            val[fieldId] = event;
            return val;
        });
    }

    onSearchTermHandler(term: string) {
        this.searchTerm.set(term);
    }

    loadStoryTemplates() {
        this.store.dispatch(PlaybookApiActions.loadStoryTemplates());
    }

    loadRundownTemplates() {
        this.store.dispatch(LiveApiActions.loadRundownTemplates());
    }

    expandCollapseAll(paneContainer: StExpansionPanelComponent | MatExpansionPanel) {
        if (paneContainer.expanded) {
            this.accordion().closeAll();
        } else {
            this.accordion().openAll();
        }
    }

    isTimeFieldDisabled(item: FieldDef): boolean {
        const isTimingField = item.ID === PSTFieldNames.PSTRundownStartTime || item.ID === PSTFieldNames.PSTRundownEndTime;
        const isArchived = this.rundownArchived();
        const hasReadOnlyValue = item?.ReadOnlyValue;
        const hasReadOnlyCriteria = this.localRecord()?.readOnlyCriteria?.[item.ID];

        if (isTimingField && isArchived) {
            return true;
        }
        return !isTimingField && (hasReadOnlyValue || hasReadOnlyCriteria || isArchived);
    }
}
